"use client";

import { createContext, useContext, useEffect, useState, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { Riva, type RivaMood } from "@/components/curriva/riva/riva";

const AuthMascotContext = createContext<{
  passwordFocused: boolean;
  disappointed: boolean;
  peeking: boolean;
  working: boolean;
  succeeded: boolean;
  focusPassword: (focused: boolean) => void;
  fail: () => void;
  reset: () => void;
  work: (busy: boolean) => void;
  succeed: () => void;
} | null>(null);

const motionQuery = "(prefers-reduced-motion: reduce)";
function subscribeMotion(callback: () => void) {
  const media = window.matchMedia(motionQuery);
  media.addEventListener("change", callback);
  return () => media.removeEventListener("change", callback);
}

function MascotState({ children }: { children: React.ReactNode }) {
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [disappointed, setDisappointed] = useState(false);
  const [peeking, setPeeking] = useState(false);
  const [working, setWorking] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const reducedMotion = useSyncExternalStore(subscribeMotion, () => window.matchMedia(motionQuery).matches, () => true);

  useEffect(() => {
    if (!passwordFocused || disappointed || reducedMotion) return;
    let timer: ReturnType<typeof setTimeout>;
    function schedulePeek() {
      timer = setTimeout(() => {
        if (document.hidden) { schedulePeek(); return; }
        setPeeking(true);
        timer = setTimeout(() => {
          setPeeking(false);
          schedulePeek();
        }, 900);
      }, 2500 + Math.random() * 1500);
    }
    schedulePeek();
    return () => clearTimeout(timer);
  }, [passwordFocused, disappointed, reducedMotion]);

  return <AuthMascotContext value={{
    passwordFocused,
    disappointed,
    peeking: peeking && !reducedMotion,
    working,
    succeeded,
    focusPassword: (focused) => { setPasswordFocused(focused); setPeeking(false); },
    fail: () => { setDisappointed(true); setPeeking(false); },
    reset: () => { setDisappointed(false); setPeeking(false); setSucceeded(false); },
    work: setWorking,
    succeed: () => { setSucceeded(true); setDisappointed(false); },
  }}>{children}</AuthMascotContext>;
}

export function AuthMascotProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  // Auth layouts persist across navigation; each form needs a fresh expression.
  return <MascotState key={pathname}>{children}</MascotState>;
}

export function useAuthMascot() {
  const state = useContext(AuthMascotContext);
  if (!state) throw new Error("Auth mascot requires AuthMascotProvider");
  return state;
}

export function AuthMascot() {
  const { passwordFocused, disappointed, peeking, working, succeeded } = useAuthMascot();
  const mood: RivaMood = succeeded ? "celebrate" : working ? "working" : disappointed ? "sad" : passwordFocused ? peeking ? "peeking" : "looking-away" : "idle";
  return <Riva mood={mood} followPointer={!passwordFocused && !disappointed} decorative />;
}
